/**
 * OTRIX Play — aviso de descanso basado en el ACSM (Resistance Training Guidelines 2026), la misma
 * evidencia con la que el generador arma la rutina.
 *
 * El descanso PRESCRITO ya codifica el objetivo (`SCHEMES` de PlayProfileMutation: hipertrofia
 * 90-120 s, metabólico 30-60 s, fuerza más largo), así que la zona se deduce de él y no hace falta
 * pedir el perfil desde el runner.
 *
 * El aviso es SIEMPRE informativo: nunca bloquea ni retrasa el botón de saltar el descanso.
 */

import { REST_SKIP_RATIO } from 'store/modules/playWorkout';

// Cortes entre zonas (s). El 75 por defecto del motor (REST_BETWEEN_SETS) cae en hipertrofia.
const METABOLIC_MAX = 60;
const STRENGTH_MIN = 150;

/** Zona de entrenamiento que representa un descanso prescrito. */
export const restZoneOf = (prescribed) => {
  const p = Number(prescribed) || 0;
  if (p >= STRENGTH_MIN) return 'strength';
  if (p <= METABOLIC_MAX) return 'metabolic';
  return 'hypertrophy';
};

/** Qué dice el ACSM para esa zona. Una frase, sin sermón. */
const zoneBody = (t, zone) => {
  if (zone === 'metabolic') {
    return t('restAdvice.zoneMetabolic', {
      ns: 'play',
      defaultValue: 'Aquí el descanso corto es parte del estímulo: el ACSM sugiere 30 a 60 s en trabajo metabólico, pero por debajo de eso se cae la técnica.'
    });
  }
  if (zone === 'strength') {
    return t('restAdvice.zoneStrength', {
      ns: 'play',
      defaultValue: 'En fuerza el ACSM pide descansos largos, de 2 a 5 min: la potencia depende de recuperar el sistema nervioso, no solo el músculo.'
    });
  }
  return t('restAdvice.zoneHypertrophy', {
    ns: 'play',
    defaultValue: 'El ACSM recomienda 90 a 120 s entre series para hipertrofia: con menos, la siguiente serie pierde repeticiones y con ellas el volumen que hace crecer el músculo.'
  });
};

/**
 * Aviso en vivo del descanso en curso. `prescribed` y `done` en segundos.
 *  - level 'short': todavía va muy por debajo de lo prescrito (saltar ahora recorta el estímulo).
 *  - level 'ready': ya descansó lo suficiente, puede seguir cuando quiera.
 * Devuelve null si no hay descanso prescrito con el que comparar.
 */
export const restAdviceFor = (t, { prescribed, done }) => {
  const target = Number(prescribed) || 0;
  const rested = Math.max(0, Number(done) || 0);
  if (target <= 0) return null;

  const zone = restZoneOf(target);
  if (rested >= target * REST_SKIP_RATIO) {
    return {
      level: 'ready',
      zone,
      title: t('restAdvice.readyTitle', { ns: 'play', defaultValue: 'Descanso suficiente' }),
      body: t('restAdvice.readyBody', {
        ns: 'play',
        prescribed: target,
        defaultValue: 'Ya cubriste lo que pide tu objetivo ({{prescribed}} s). Sigue cuando estés listo.'
      })
    };
  }

  return {
    level: 'short',
    zone,
    title: t('restAdvice.shortTitle', {
      ns: 'play',
      seconds: Math.max(1, Math.round(target - rested)),
      defaultValue: 'Te faltan {{seconds}} s del descanso sugerido'
    }),
    body: zoneBody(t, zone)
  };
};

/**
 * Resumen de los descansos cortados en todo el entrenamiento, para el cierre.
 *
 * Solo cuenta los descansos ENTRE SERIES: el último de cada ejercicio es el cambio de ejercicio
 * (un estimado del motor, no una prescripción del objetivo) y casi nadie lo espera completo, así
 * que contarlo haría saltar el aviso en toda sesión y lo volvería ruido.
 *
 * Devuelve null cuando no hay nada que decir.
 */
export const restSkipSummary = (t, exercises) => {
  let skipped = 0;
  let total = 0;
  let prescribedSum = 0;

  (exercises || []).forEach((ex) => {
    const rests = ex?.restDone || [];
    const flags = ex?.restSkipped || [];
    const lastIndex = (Number(ex?.sets) || 1) - 1;
    rests.forEach((value, index) => {
      // Los huecos del array son descansos que nunca ocurrieron (series sin hacer o salida a
      // media sesión); ojo con `Number(null) === 0`, que sí es finito.
      if (index >= lastIndex || value === null || value === undefined || !Number.isFinite(Number(value))) return;
      total += 1;
      if (flags[index]) {
        skipped += 1;
        prescribedSum += Number(ex?.restBetweenSets) || 0;
      }
    });
  });

  if (skipped === 0) return null;

  const zone = restZoneOf(Math.round(prescribedSum / skipped));
  return {
    skipped,
    total,
    zone,
    title: t('restAdvice.skippedTitle', {
      ns: 'play',
      count: skipped,
      total,
      defaultValue: 'Cortaste {{count}} de {{total}} descansos'
    }),
    body: zoneBody(t, zone)
  };
};
